import React, { useContext, useEffect, useState } from 'react';
import { Button } from 'src/components/ui/button';
import { api } from 'src/api';
import { AppContext } from 'src/hooks/AppProvider';
import { Tag, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Form, Input, Radio, Checkbox, Select } from 'antd';
import { ERuleType, EAttributionField, EUserTier } from 'src/interfaces';
import { useQuery } from '@tanstack/react-query';

const ATTRIBUTION_SHEET_URL =
  'https://docs.google.com/spreadsheets/d/1b1zrFzitDtxuRMCLJXPX916MQo4-MQWJHeHJ63CgG5U/edit?gid=0#gid=0';

type AddressProposeAttributionProps = {
  address: string | undefined;
};

export const AddressProposeAttribution = ({
  address,
}: AddressProposeAttributionProps) => {
  const { user } = useContext(AppContext);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form] = Form.useForm();

  // Fetch entities SOT only when modal is opened
  const { data: entitiesSot = [] } = useQuery({
    queryKey: ['entitiesSot'],
    queryFn: async () => {
      const data = await api.entity.getEntitiesSot();
      return data;
    },
    enabled: isOpen, // Only fetch when modal is opened
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
    }
  }, [isOpen, form]);

  const handleSubmit = async () => {
    if (!address) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const values = await form.validateFields();
      const data = {
        address,
        entity: values.entity,
        note: values.note,
        user: user?.email,
        isHCCluster: values.makeHcCluster,
        field: values.attributionType as EAttributionField,
        rules: values.attributionMethod as ERuleType[],
      };

      await api.entity.submitAttribution(data);
      toast.success(
        <div>
          Attribution has been successfully saved to{' '}
          <a
            href={ATTRIBUTION_SHEET_URL}
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Attribution Proposal Google Sheet
          </a>
          .
        </div>,
      );
      setIsOpen(false);
    } catch (error) {
      console.error(error);
      setError('Failed to submit attribution. Please try again.');
      toast.error('Failed to submit attribution');
    } finally {
      setLoading(false);
    }
  };

  if (user?.accountType !== EUserTier.INTERNAL) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <Tag className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Propose Attribution</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          {error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-500">
              {error}
            </div>
          )}

          <Form form={form} layout="vertical">
            <Form.Item label="Address">
              <Input value={address} disabled />
            </Form.Item>

            <Form.Item
              name="attributionType"
              label="Attribution Field"
              rules={[
                {
                  required: true,
                  message: 'Please select an attribution type',
                },
              ]}
              initialValue="entity"
            >
              <Radio.Group>
                <Radio value="entity">Entity</Radio>
                <Radio value="beneficial-owner">Beneficial Owner</Radio>
                <Radio value="custodian">Custodian</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name="attributionMethod"
              label="Attribution Rule"
              rules={[
                {
                  required: true,
                  message: 'Please select an attribution rule',
                },
              ]}
            >
              <Checkbox.Group>
                <Checkbox value="manual">Manual</Checkbox>
                <Checkbox value="incoming">Incoming</Checkbox>
                <Checkbox value="outgoing">Outgoing</Checkbox>
              </Checkbox.Group>
            </Form.Item>

            <Form.Item
              name="entity"
              label="Canonical"
              rules={[
                {
                  required: true,
                  message: 'Please enter or select a canonical',
                },
              ]}
            >
              <Select
                showSearch
                allowClear
                placeholder="Search and select an entity..."
                dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                filterOption={(input, option) =>
                  (option?.value?.toString().toLowerCase() ?? '').includes(
                    input.toLowerCase(),
                  )
                }
                loading={isOpen && entitiesSot.length === 0}
                notFoundContent={
                  isOpen && entitiesSot.length === 0
                    ? 'Loading...'
                    : 'No entities found'
                }
              >
                {entitiesSot.map((entity) => (
                  <Select.Option key={entity} value={entity}>
                    {entity}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="makeHcCluster"
              valuePropName="checked"
              initialValue={false}
            >
              <Checkbox>Is HC Cluster</Checkbox>
            </Form.Item>

            <Form.Item name="note" label="Proposal Note">
              <Input.TextArea rows={4} placeholder="Optional" />
            </Form.Item>
          </Form>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
